import {Component, computed, effect, inject, OnInit, signal, untracked} from '@angular/core';
import {toLocalISOString} from '../../../../util/date.utils';
import {FormsModule} from '@angular/forms';
import {DatePicker} from 'primeng/datepicker';
import {Card} from 'primeng/card';
import {Textarea} from 'primeng/textarea';
import {AutoComplete, AutoCompleteCompleteEvent} from "primeng/autocomplete";
import {CreateTaBaseComponent} from '../create-ta-base-component.directive';
import {BaseOption, ProjectDetails, UserDetails, UserGroupDetails} from '../../../../api';
import {DatePipe} from '@angular/common';
import {Select} from 'primeng/select';
import {RadioButton} from 'primeng/radiobutton';
import {FlightTripType} from '../../../../util/app.types';
import {ErrorMessageService} from '../../../../services/error-message.service';

@Component({
  selector: 'create-ta-details',
    imports: [
        FormsModule,
        DatePicker,
        Card,
        Textarea,
        AutoComplete,
        DatePipe,
        Select,
        RadioButton,
    ],
  templateUrl: './create-ta-details.component.html',
  styleUrl: './create-ta-details.component.scss'
})
export class CreateTaDetailsComponent extends CreateTaBaseComponent implements OnInit {
  private errorMessageService = inject(ErrorMessageService);

  constructor() {
    super();
    effect(() => {
      this.filteredProjects = this.availableProjects();
    });

    // A sole option leaves the p-select disabled, so the user cannot pick it, and the server never
    // derives a CEO's or General Manager's primary approver. Mirrors the count-of-one auto-set
    // loadAvailableApprovers does for the secondary approver.
    effect(() => {
      if (this.isReadOnly() || !this.requiresPrimaryApprover()) return;
      const options = this.availablePrimaryApprovers();
      if (options.length !== 1) return;
      const sole = options.at(0)!;
      const submission = this.taSubmission();
      // Both fields, because updateField writes both and choosing a project clears the secondary
      // ("Unsetting approver" in loadAvailableApprovers) — which lands after this has already run.
      // Flight-only TAs have no secondary approver.
      if (submission?.primaryApprover?.id === sole.id
        && (this.isFlightOnly() || submission?.secondaryApprover?.id === sole.id)) return;
      this.updateField('primaryApprover', {value: sole});
    });

    // Flight-only projects depend on the traveller's manager chain, so reload on a traveller change.
    effect(() => {
      const travellerId = this.isFlightOnly() && !this.isReadOnly()
        ? this.taSubmission()?.primaryTraveller?.details?.id ?? undefined
        : undefined;
      untracked(() => this.loadFlightOnlyProjects(travellerId));
    });

    // Flight-only: a traveller whose manager is the CEO needs a manually chosen GM approver.
    effect(() => {
      if (this.isFlightOnly() && !this.ceoIdsLoaded) {
        this.ceoIdsLoaded = true;
        untracked(() => this.taSearchDatasource.loadCeoIds().subscribe({
          next: ids => this.ceoIds.set(ids),
          error: error => {
            // Without CEO ids the GM picker can't appear, so tell the user rather than leave them stuck.
            console.error('Error getting CEO options: ', error);
            this.ceoIdsLoaded = false;
            this.errorMessageService.showTemporaryError('Could not load approver options. Reload the page to try again.', 5000);
          },
        }));
      }
    });
    effect(() => {
      if (this.isFlightOnly() && !this.isReadOnly() && this.requiresPrimaryApprover()) {
        untracked(() => this.loadPrimaryApprovers());
      }
    });
  }

  // Flight-only state; Standard TAs use store.availableProjects.
  private flightOnlyProjects = signal<ProjectDetails[] | undefined>(undefined);
  noFlightOnlyProjects = computed<boolean>(() => this.flightOnlyProjects()?.length === 0);
  private flightOnlyProjectsTravellerId: string | undefined;
  private ceoIds = signal<string[]>([]);
  private ceoIdsLoaded = false;
  hasPrimaryTraveller = computed<boolean>(() => !!this.taSubmission()?.primaryTraveller?.details?.id);
  flightTripType = this.taSubmissionDatasource.flightTripType;
  // Same-day return isn't supported, so the two flights are at least a day apart.
  secondFlightDate = computed<Date | null>(() =>
    this.flightTripType() === 'return' && !this.isSameDay(this.startDate(), this.endDate()) ? this.endDate() : null);
  secondFlightMinDate = computed<Date | null>(() => this.addDays(this.startDate(), 1));
  firstFlightMaxDate = computed<Date | null>(() => this.addDays(this.secondFlightDate(), -1));

  project = computed<ProjectDetails | undefined>(() => {
    return this.availableProjects().find((project) => project.id === this.taSubmission()?.projectId)
  });

  //Support data objects
  availableProjects = computed<ProjectDetails[]>(() => (
    this.isFlightOnly() ? this.flightOnlyProjects() ?? [] : this.store.availableProjects()));
  filteredProjects: ProjectDetails[] = [];
  private baseApprovers = signal<UserDetails[]>([]);
  availableApprovers = computed<UserDetails[]>(() => {
    const primaryId = this.taSubmission()?.primaryTraveller?.details?.id;
    return primaryId ? this.baseApprovers().filter(u => u.id !== primaryId) : this.baseApprovers();
  });
  filteredApprovers: UserDetails[] = [];
  availablePurposes = computed<BaseOption[]>(() => (this.store.session().details?.travelReasonOptions ?? []));
  filteredPurposes: BaseOption[] = [];

  //General Managers and CEO groups have special interactions on this page.
  //They see a primary approver dropdown card and no longer see the secondary approver dropdown like everyone else.
  currentGroups = computed<UserGroupDetails[]>(() => {
    return this.taSubmission()?.primaryTraveller?.details?.permissionSummary?.groups ?? [];
  });
  //The primary approver scenario is as follows
  //If the scenario is 'ceo' or 'both'   -> the current user is a CEO             -> requires approval/ approval options as general managers
  //If the scenario is 'generalManagers' -> the current user is a General Manager -> requires approval/ approval options as CEOs
  primaryApproverScenario = computed<'ceo' | 'generalManagers' | 'both' | 'none'>(() => {
    if (this.currentGroups().filter(group => group.code === 'ceo' ||  group.code === 'generalManagers').length > 1) {
      return 'both';
    }
    if (this.currentGroups().filter(group => group.code === 'ceo').length > 0) {
      return 'ceo';
    }
    if (this.currentGroups().filter(group => group.code === 'generalManagers').length > 0) {
      return 'generalManagers';
    }
    return 'none';
  });
  requiresPrimaryApprover = computed<boolean>(() => {
    if (this.primaryApproverScenario() !== 'none') {
      return true;
    }
    // Mirrors the server's RequiresManualPrimaryApproverAsync for flight-only TAs.
    const managerId = this.taSubmission()?.primaryTraveller?.details?.managerId;
    return this.isFlightOnly() && !!managerId && this.ceoIds().includes(managerId);
  });
  availablePrimaryApprovers = computed<UserDetails[]>(() => {
    const primaryId = this.taSubmission()?.primaryTraveller?.details?.id;
    return primaryId
      ? this.store.availablePrimaryApprovers().filter(u => u.id !== primaryId)
      : this.store.availablePrimaryApprovers();
  });

  override ngOnInit(): void {
    super.ngOnInit();
    // Flight-only loads its own projects and approvers from effects.
    if (this.isFlightOnly()) {
      return;
    }
    this.taSearchDatasource.loadProjectOptions();

    if (!!this.taSubmission()?.projectId) {
      this.loadAvailableApprovers(this.taSubmission()!.projectId!);
    }

    if (this.requiresPrimaryApprover()) {
      this.loadPrimaryApprovers();
    }
  }

  updateField(field: string, event: any) {
    let value: string | null | undefined;
    let localValue: any;

    if (field === 'projectId') {
      let project = event.value as ProjectDetails;
      value = project?.id;
      localValue = value;
    } else if (field === 'primaryApprover') {
      localValue = event.value as UserDetails;
      value = localValue?.id?.toString();
      // The server rejects any secondary approver on a flight-only TA.
      if (!this.isFlightOnly()) {
        this.taSubmissionDatasource.saveTaFieldString('secondaryApprover', value, localValue);
      }
    } else if (field === 'secondaryApprover') {
      localValue = event.value as UserDetails;
      value = localValue.id?.toString();
    } else if (field === 'startDate' || field === 'endDate') {
      const d = event as Date;
      value = toLocalISOString(d);
      localValue = value;
    } else if (field === 'purpose') {
      value = event.value.value;
      localValue = value;
    } else if (field === 'purposeNotes') {
      value = (event.target as HTMLInputElement).value;
      localValue = value;
    } else {
      value = event.value ?? '';
      localValue = value ?? '';
    }
    this.taSubmissionDatasource.saveTaFieldString(field, value, localValue);
  }

  updateProject(field: string, event: any): void {
    let project = event.value as ProjectDetails;
    if (project.id) {
      // Flight-only: the server sets the approver; loadAvailableApprovers would save a secondary.
      if (!this.isFlightOnly()) {
        this.loadAvailableApprovers(project.id, 'projectChanged');
      }
      this.updateField(field, event);
    }
  }

  setFlightTripType(type: FlightTripType): void {
    this.taSubmissionDatasource.setFlightTripChoice(type);
    const start = this.startDate();
    if (type === 'oneWay' && !!start && !this.isSameDay(start, this.endDate())) {
      this.updateField('endDate', start);
    }
  }

  updateFirstFlight(date: Date): void {
    if (this.flightTripType() === 'return') {
      this.updateField('startDate', date);
      return;
    }
    // One way stores end == start. Save in the order that keeps start <= end at every step.
    const end = this.endDate();
    if (!!end && date.getTime() > end.getTime()) {
      this.updateField('endDate', date);
      this.updateField('startDate', date);
    } else {
      this.updateField('startDate', date);
      this.updateField('endDate', date);
    }
  }

  private loadFlightOnlyProjects(travellerId: string | undefined): void {
    if (travellerId === this.flightOnlyProjectsTravellerId) {
      return;
    }
    this.flightOnlyProjectsTravellerId = travellerId;
    this.flightOnlyProjects.set(undefined);
    if (!travellerId) {
      return;
    }
    this.taSearchDatasource.loadFlightOnlyProjectOptions(travellerId).subscribe({
      next: response => {
        // Dropped if the traveller changed while this was in flight.
        if (travellerId !== this.flightOnlyProjectsTravellerId) return;
        if (response.success) {
          this.flightOnlyProjects.set(response.data ?? []);
        } else {
          console.error('Error getting flight-only projects: ', response.error);
        }
      },
      error: error => console.error(error),
    });
  }

  private isSameDay(a: Date | null, b: Date | null): boolean {
    return !!a && !!b && a.toDateString() === b.toDateString();
  }

  private addDays(date: Date | null, days: number): Date | null {
    if (!date) {
      return null;
    }
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  filterProjects(event: AutoCompleteCompleteEvent) {
    this.filteredProjects = this.availableProjects().filter(project => project.code?.toLowerCase().includes(event.query.toLowerCase()));
  }

  filterApprovers(event: AutoCompleteCompleteEvent) {
    this.filteredApprovers = this.availableApprovers().filter(approver => (approver?.firstName + ' ' + approver?.surname).toLowerCase().includes(event.query.toLowerCase()));
  }

  filterPurposes(event: AutoCompleteCompleteEvent) {
    this.filteredPurposes = this.availablePurposes().filter(option => (option?.label + ' ' + option?.value).toLowerCase().includes(event.query.toLowerCase()));
  }

  loadPrimaryApprovers(): void {
    // Remove primary approvers caching for auto-set feature.
    this.store.updateAvailablePrimaryApprovers([]);
    // The CEO never approves a flight-only TA, so only GMs are offered.
    if (this.isFlightOnly() || ['ceo', 'both'].includes(this.primaryApproverScenario())) {
      this.taSearchDatasource.loadGeneralManagerOptions();
    } else {
      this.taSearchDatasource.loadCeoOptions();
    }
  }

  loadAvailableApprovers(pid: string, projectChanged?: 'projectChanged') {
    this.taSearchDatasource.loadApproverOptions(pid).subscribe({
      next: response => {
        if (response.success) {
          this.baseApprovers.set(response.data ?? []);
          if (projectChanged === 'projectChanged') {
            if (response.data && response.data.length === 1) {
              let approver = response.data[0];
              this.taSubmissionDatasource.saveTaFieldString('secondaryApprover', approver.id, approver);
            } else if (response.data && response.data.length > 0) {
              console.log('Unsetting approver');
              this.taSubmissionDatasource.saveTaFieldString('secondaryApprover', null, null);
            }
          }
        } else {
          console.error('Error getting approvers: ', response);
        }
      },
      error: error => {
        console.error(error);
      }
    })
  }
}

